import React from 'react';
import styled from 'styled-components';
import { PropTypes } from 'prop-types';
import Constants from '../../submodules/logictry_config/constants';
import WindowSize from '../../services/WindowSize';
import Connect from '../../services/Connect';
import AppRoot from '../../services/AppRoot';
import Navigation from '../../services/Navigation';
import FixedFullscreenDiv from '../FixedFullscreenDiv';
import ReactDOM from 'react-dom';
import { CloseIcon } from '../../styledhtml/Icon';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import { twModalSurfaceShadow } from '../../styledhtml/Tailwind';
const FADE_DURATION = 200; // fade out duration matches CSS transition

/* --logictry-popup-safe-* are defined by the native Ionic host (0 on web). */
const Wrapper = styled(FixedFullscreenDiv)`
  z-index: 5;
  display: flex;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  padding-top: var(--logictry-popup-safe-top, 0px);
  padding-bottom: var(--logictry-popup-safe-bottom, 0px);
  transition: opacity 0.2s ease;
  h1, h2 {
    margin: 1rem 0;
  }
`;

const ClearWrapper = styled(FixedFullscreenDiv)`
  z-index: 5;
`;

const Card = styled.div`
  background-color: ${Constants.SurfaceColor};
  border-radius: ${Constants.BoxRadius}px;
  overflow: auto;
  position: relative;
  max-width: calc(100vw - 3rem);
  ${Constants.PopupMaxHeight}
  transition: opacity 0.2s ease;
  box-shadow: ${twModalSurfaceShadow};
  padding-top: 0.25rem;
  > i {
    position: absolute;
    top: -3px;
    right: -3px;
  }
`;

const AnchorCard = styled.div`
  overscroll-behavior: none;
  z-index: 5;
  max-width: calc(100vw - 3rem);
  background-color: ${Constants.SurfaceColor};
  box-shadow: ${twModalSurfaceShadow};
  border-radius: 8px;
  overflow: auto;
  ${Constants.PopupMaxHeight}
  transition: opacity 0.2s ease;
  > i {
    position: absolute;
    top: -3px;
    right: -3px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: calc(0.25rem + var(--logictry-popup-safe-top, 0px));
  right: 1rem;
  font-size: 2rem;
  color: white;
  cursor: pointer;
`;

const popupsShowing = new Set();

class Popup extends LogictryComponent {
  constructor() {
    super();
    this.innerHeight = window.innerHeight;
    this.innerWidth = window.innerWidth;
  }

  state = {
    opacity: 0 // start invisible, fade-in on mount
  };

  componentDidMount() {
    Navigation.onStateUpdate(this.closePopup);

    const { preventDeletion, anchorEl, showFullscreen } = this.props;

    if (preventDeletion) {
      document.addEventListener('pointermove', this.preventDeletionHandler);
      document.addEventListener('pointerdown', this.preventDeletionHandler);
    }

    if (!anchorEl) {
      if (showFullscreen) Connect.toggleFullscreen();
      else this.centerPopup();
    }

    const { mobile } = WindowSize;

    if (mobile) {
      popupsShowing.add(this);
      AppRoot.disableScroll = true;
    }
  }

  componentWillUnmount() {
    Navigation.offStateUpdate(this.closePopup);
    document.removeEventListener('pointermove', this.onMouseMove);
    document.removeEventListener('pointermove', this.preventDeletionHandler);
    document.removeEventListener('pointerdown', this.preventDeletionHandler);

    this.removeInterval();

    const { anchorEl, showFullscreen } = this.props;

    if (!anchorEl && showFullscreen) Connect.toggleFullscreen();

    if (popupsShowing.has(this)) {
      popupsShowing.delete(this);
      if (popupsShowing.size === 0) AppRoot.disableScroll = false;
    }
  }

  // ---------------------------
  // FADE OUT LOGIC
  // ---------------------------
  closePopup = () => {
    if (this.closeAlreadyCalled) return;
    this.closeAlreadyCalled = true;

    // Trigger fade-out animation
    this.setState({ opacity: 0 });

    // Delay actual close until animation finishes
    setTimeout(() => {
      const { onClose } = this.props;
      if (onClose) onClose();
    }, FADE_DURATION);
  };

  removeInterval = () => {
    if (!this.__boundsInterval) return;
    clearInterval(this.__boundsInterval);
    this.__boundsInterval = null;
  };

  preventDeletionHandler = (e) => {
    const root = document.getElementById('root');
    if (root.contains(e.target)) window.location.reload(true);
  };

  // ---------------------------
  // FADE-IN (unchanged)
  // ---------------------------
  onRef = (ref) => {
    const { allowClickThrough } = this.props;
    if (!ref) return;

    if (allowClickThrough)
      document.addEventListener('pointermove', this.onMouseMove);

    // Trigger fade-in
    setTimeout(() => {
      this.setState({ opacity: 1 });
    }, 10);
  };

  onCardRef = (ref) => {
    if (!ref) return;
    const { anchorEl } = this.props;

    this.cardRef = ref;
    this.anchorBounds = anchorEl.getBoundingClientRect();

    this.__boundsInterval = setInterval(() => {
      this.__getBounds();
    }, 10);

    this.__getBounds();
  };

  onFullscreenCardRef = (e) => {
    this.fullscreenCardRef = e;
    this.centerPopup();
  };

  centerPopup = () => {
    const { anchorEl, showFullscreen } = this.props;
    if (anchorEl || showFullscreen) return;
    if (!this.fullscreenCardRef) return;
    if (this.centerPopupMessageSent) return;

    this.centerPopupMessageSent = true;

    const { top } = this.fullscreenCardRef.getBoundingClientRect();
    Connect.centerContent(top + window.pageYOffset);
  };

  __getBounds = () => {
    const { anchorBottomRight } = this.props;
    const cardBounds = this.cardRef.getBoundingClientRect();

    {
      const left =
        anchorBottomRight
          ? this.anchorBounds.right - cardBounds.width
          : this.anchorBounds.left;

      const right = left + cardBounds.width;
      const top = this.anchorBounds.bottom;
      const bottom = this.anchorBounds.bottom + cardBounds.height;

      const offsetFromSide = 20;

      let translateX = 0;
      let translateY = 0;

      if (right > this.innerWidth - offsetFromSide)
        translateX = this.innerWidth - offsetFromSide - right;
      else if (left < offsetFromSide)
        translateX = offsetFromSide - left;

      if (bottom > this.innerHeight - offsetFromSide)
        translateY = this.innerHeight - offsetFromSide - bottom;
      else if (top < offsetFromSide)
        translateY = offsetFromSide - top;

      this.cardRef.style.left = `${left + translateX}px`;
      this.cardRef.style.top = `${top + translateY}px`;
    }

    {
      const { top, left, right, bottom } = this.cardRef.getBoundingClientRect();
      const newBounds = {
        top: top - this.anchorBounds.height,
        left: left - this.anchorBounds.height,
        right: right + this.anchorBounds.height,
        bottom: bottom + this.anchorBounds.height,
      };

      if (
        this.bounds &&
        this.bounds.top === newBounds.top &&
        this.bounds.bottom === newBounds.bottom &&
        this.bounds.left === newBounds.left &&
        this.bounds.right === newBounds.right
      ) {
        this.cardRef.style.opacity = 1;
        this.removeInterval();
      }

      this.bounds = newBounds;
    }
  };

  onMouseMove = (e) => {
    const { mobile } = WindowSize;
    const { allowMoveClose } = this.props;

    if (mobile) return;
    if (!allowMoveClose) return;
    if (!this.cardRef || !this.bounds) return;

    const { top, left, right, bottom } = this.bounds;
    const { pageX, pageY } = e;

    if (pageX < left || pageX > right || pageY < top || pageY > bottom)
      this.closePopup();
  };

  onClickBackground = (e) => {
    const { allowBackgroundClose, onClose } = this.props;
    if (!(allowBackgroundClose && onClose)) return;

    e.stopPropagation();
    e.preventDefault();
    this.closePopup();
  };

  render() {
    const {
      onClose,
      children,
      anchorEl,
      showCloseButton,
      allowClickThrough,
      anchorBorderRadius,
    } = this.props;

    const { opacity } = this.state;
    const { mobile } = WindowSize;
    const { innerHeight } = Connect;

    // Native Ionic host defines the safe vars (0 on web) so cards keep the
    // web-like 3rem gaps inside the safe area instead of filling under the
    // status bar / home indicator.
    const maxHeight =
      (innerHeight && `calc(${innerHeight}px - 6rem - var(--logictry-popup-safe-top, 0px) - var(--logictry-popup-safe-bottom, 0px))`) ||
      (Constants.isIphone && `calc(${this.innerHeight}px - 6rem - var(--logictry-popup-safe-top, 0px) - var(--logictry-popup-safe-bottom, 0px))`) ||
      null;

    const html = anchorEl ? (
      <>
        <ClearWrapper
          ref={this.onRef}
          style={{
            pointerEvents: !mobile && allowClickThrough ? 'none' : 'unset',
            position: 'fixed',
            opacity,
            transition: 'opacity 0.2s ease',
          }}
          onMouseMove={this.onMouseMove}
          onClick={(e) => {
            e.stopPropagation();
            this.closePopup();
          }}
        />
        <AnchorCard
          ref={this.onCardRef}
          style={{
            maxHeight,
            opacity,
            backgroundColor: Constants.SurfaceColor,
            position: 'fixed',
            borderRadius: anchorBorderRadius,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {children}
        </AnchorCard>
      </>
    ) : (
      <Wrapper
        ref={this.onRef}
        style={{
          backgroundColor: `rgba(32,33,36,.6)`,
          position: 'fixed',
          height: Constants.isIphone ? `${this.innerHeight}px` : null,
          opacity,
        }}
        onClick={(e) => {
          e.stopPropagation();
          this.onClickBackground(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={this.onMouseMove}
      >
        <Card
          ref={this.onFullscreenCardRef}
          style={{
            maxHeight,
            opacity,
            backgroundColor: Constants.SurfaceColor,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {children}
        </Card>

        {showCloseButton && onClose && (
          <CloseButton onClick={this.closePopup}>
            <CloseIcon
              style={{
                width: 20,
                height: 20,
                filter: 'invert(1)',
                margin: '12px 8px',
              }}
            />
          </CloseButton>
        )}
      </Wrapper>
    );

    return ReactDOM.createPortal(html, document.body);
  }
}

Popup.propTypes = {
  children: PropTypes.any,
  onClose: PropTypes.func,
  allowBackgroundClose: PropTypes.bool,
  allowClickThrough: PropTypes.bool,
  allowMoveClose: PropTypes.bool,
  showCloseButton: PropTypes.bool,
  anchorEl: PropTypes.any,
  anchorBottomRight: PropTypes.bool,
  anchorBorderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  preventDeletion: PropTypes.bool,
  showFullscreen: PropTypes.bool,
};

export default Popup;
